import { useMemo } from 'react';
import clsx from 'clsx';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useCustomerCyo } from '@hungryroot/cyo';
import { useModalStore } from '@hungryroot/global-state';
import { Loading } from '@hungryroot/loading-component-primitives';
import { EXPAND_ORDER_WITH_BUNDLE_DETAILS } from '@hungryroot/orders';
import { OrderStatus } from '@hungryroot/rest';
import {
  DynamicConfigs,
  useDynamicConfigs,
  useFavoritePairings,
  useIdentify,
  useNextOrderV3,
} from '@hungryroot/rest-hooks';
import { EDIT_DELIVERY_RECIPES_ROUTE } from '@hungryroot/routes';
import { Paragraph } from '@hungryroot/ui-kit';
import { useIsMobilePopsicleEnabled } from '../../../exp-242-popsicle/hooks/useIsMobilePopsicleEnabled';
import { useSprinklesWalletLayout } from '../../../exp-245-rewards/hooks/useSprinklesWalletLayout';
import {
  CustomizationStates,
  useCustomization,
} from '../../../providers/CustomizationProvider/CustomizationProvider';
import { Cart } from '../../cart/Cart';
import { CartCookbookBadgesProvider } from '../../cart/CartCookbookBadgesContext';
import { getCookbookPairingIds } from '../../cart/getCookbookPairingIds';
import { CreditsInfoBanner } from '../CreditsInfoBanner/CreditsInfoBanner';
import { DeliveryCard } from '../DeliveryCard/DeliveryCard';
import { FloatingDate } from './FloatingDate';
import { FloatingTotalCredits } from './FloatingTotalCredits';
import { useElementInView } from './useElementInView';

import styles from './CartTabBody.module.css';

/**
 * Height (px) of the floating bottom nav region. The credits overlay observer
 * subtracts this from the bottom of the viewport so the in-flow total credits
 * only counts as "in view" once it clears the nav — otherwise it would read as
 * visible while still hidden behind the nav. Mirrors the nav-height fallback
 * used for the overlay's `bottom` offset in CSS.
 */
const FLOATING_NAV_OFFSET_PX = 88;
const CREDITS_ROOT_MARGIN = `0px 0px -${FLOATING_NAV_OFFSET_PX}px 0px`;

/**
 * Hand off the floating credits to the in-flow total once ~20% of the latter
 * has cleared the nav (not just the very first pixel of its top edge), so the
 * overlay doesn't linger once the real total starts coming into view.
 */
const CREDITS_VISIBLE_RATIO = 0.2;

const EMPTY_PAIRING_IDS: ReadonlySet<number> = new Set();

export function CartTabBody() {
  const history = useHistory();
  const { setRescheduleModal } = useModalStore();
  const { customerData } = useIdentify();
  const customization = useCustomization();
  const { data: customerCYO, isLoading: cyoLoading } = useCustomerCyo();
  const { order: nextOrder, isLoading: nextOrderLoading } = useNextOrderV3({
    expand: EXPAND_ORDER_WITH_BUNDLE_DETAILS,
  });
  const { isEnabled: isMobilePopsicleEnabled } = useIsMobilePopsicleEnabled();
  const { isInSprinklesVariant } = useSprinklesWalletLayout();
  // Pull the customer's favorites and join against the cart locally; only
  // fetched on the exp-242 mobile orders tab where the badge can show.
  const { data: favoritePairings } = useFavoritePairings({
    enabled: isMobilePopsicleEnabled,
  });

  // Same "credit minimum" source the cart footer uses (InsufficientOrderThreshold);
  // the floating credits shows the credit-min warning when the cart is under it.
  const { data: thresholdData } = useDynamicConfigs({
    name: [DynamicConfigs.InsufficientOrderThreshold],
  });
  const creditMinimum = Number(thresholdData?.results[0]?.value) || 0;

  const isCustomizationReady =
    customization.state === CustomizationStates.Ready ||
    customization.state === CustomizationStates.Skipped ||
    customization.state === CustomizationStates.Shuffling;

  /**
   * The cart summary (and thus the [data-smart-cart-anchor] marker) only
   * renders once the cart itself is available — i.e. Ready/Skipped, not while
   * Shuffling. Gating the credits observer on this ensures it attaches after
   * the anchor mounts; otherwise it would observe nothing and never fire.
   */
  const isCartAvailable =
    customization.state === CustomizationStates.Ready ||
    customization.state === CustomizationStates.Skipped;

  /**
   * The floating overlays observe markers placed by DeliveryCard (`data-cart-anchor="date"`)
   * and the cart-summary section in DefaultFooter (`[data-smart-cart-anchor]`). They only
   * begin observing once the relevant in-flow anchor has rendered, so the
   * overlays don't flash in before the content mounts.
   */
  const tabBodyReady =
    !nextOrderLoading && !!customerData && isCustomizationReady;
  const dateInView = useElementInView(
    '[data-cart-anchor="date"]',
    tabBodyReady
  );
  const creditsInView = useElementInView(
    '[data-smart-cart-anchor]',
    tabBodyReady && isCartAvailable,
    { rootMargin: CREDITS_ROOT_MARGIN, visibleRatio: CREDITS_VISIBLE_RATIO }
  );

  /**
   * Pairing ids that should show the "From your cookbook" badge — any recipe
   * in the cart that the customer has favorited, regardless of how it was
   * added. Computed only for the exp-242 mobile orders tab; empty otherwise
   * so no badge shows.
   */
  const cookbookPairingIds = useMemo(() => {
    if (!isMobilePopsicleEnabled) return EMPTY_PAIRING_IDS;

    const badgeCart =
      customization.state === CustomizationStates.Ready ||
      customization.state === CustomizationStates.Skipped
        ? customization.cart
        : null;
    if (badgeCart === null) return EMPTY_PAIRING_IDS;

    const favoritePairingIds = new Set(
      (favoritePairings?.favorite_pairings ?? []).map(
        (favorite) => favorite.pairing_id
      )
    );

    return getCookbookPairingIds({
      bundles: badgeCart.bundles,
      favoritePairingIds,
    });
  }, [customization, favoritePairings, isMobilePopsicleEnabled]);

  if (nextOrderLoading || !customerData || !isCustomizationReady) {
    return <Loading variant="inline-button" />;
  }

  if (!nextOrder) {
    return (
      <Paragraph variant="body">
        You don&rsquo;t have an upcoming order right now.
      </Paragraph>
    );
  }

  const cart =
    customization.state === CustomizationStates.Ready ||
    customization.state === CustomizationStates.Skipped
      ? customization.cart
      : null;

  const cartIsEmpty = cart
    ? (cart.bundles?.length ?? 0) === 0 && (cart.products?.length ?? 0) === 0
    : true;
  const isSkipped = nextOrder.status === OrderStatus.Skipped;
  const points = cart ? String(cart.points) : undefined;
  const isBelowCreditMinimum =
    creditMinimum > 0 && cart != null && cart.points < creditMinimum;
  const dateLabel = format(parseISO(nextOrder.preferred_date), 'EEEE, MMM d');

  // Only surface the "How do credits work?" banner on the customer's very first
  // box (sequence === 1), and only until its cutoff passes — once cutoff hits,
  // the next cart starts showing and the banner is no longer relevant. Mirrors
  // the first-order + cutoff gating used by useOnboardingProgress.
  const showCreditsBanner =
    nextOrder.sequence === 1 &&
    differenceInMinutes(parseISO(nextOrder.cutoff_datetime), new Date()) >= 0;

  const openRescheduleModal = (testid: string) =>
    setRescheduleModal({
      order: {
        ...nextOrder,
        possible_delivery_dates: nextOrder.possible_delivery_dates ?? [],
      },
      openedFromCart: true,
      testid,
    });

  const handleCalendarClick = () =>
    openRescheduleModal('orders-page-floating-date');

  return (
    <>
      <DeliveryCard
        variant="cart"
        order={nextOrder}
        points={points}
        cartIsEmpty={cartIsEmpty}
        isSkipped={isSkipped}
        onManageClick={() => openRescheduleModal('orders-page-manage')}
      />
      {showCreditsBanner && <CreditsInfoBanner orderId={nextOrder.id} />}
      <CartCookbookBadgesProvider cookbookPairingIds={cookbookPairingIds}>
        <Cart
          testid="orders-page-cart"
          className={clsx({ [styles['cart-section-fill']]: cartIsEmpty })}
          customer={customerData}
          customization={customization}
          customerCYO={customerCYO ?? null}
          isLoadingCYO={cyoLoading}
          dismiss={() => history.push(EDIT_DELIVERY_RECIPES_ROUTE)}
          standalone={false}
        />
      </CartCookbookBadgesProvider>
      {!dateInView && (
        <FloatingDate date={dateLabel} onCalendarClick={handleCalendarClick} />
      )}
      {!creditsInView && cart && (
        <FloatingTotalCredits
          cart={cart}
          customer={customerData}
          isBelowCreditMinimum={isBelowCreditMinimum}
          creditMinimum={creditMinimum}
          isSprinklesVariant={isInSprinklesVariant}
        />
      )}
    </>
  );
}
